import { useState } from "react";
import { Card } from "@/components/ui/card";
import { FinancingFilters } from "../financing/FinancingFilters";
import { FinancingTable } from "../financing/FinancingTable";
import { useValidatedAmounts } from "../financing/useValidatedAmounts";
import { startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { fr } from "date-fns/locale";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export const FinancingTab = () => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const { data: validatedAmounts = [], isLoading } = useValidatedAmounts(selectedDate);

  const { data: weeklyTotal = 0 } = useQuery({
    queryKey: ["validatedAmounts", "week"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const now = new Date();
      const weekStart = startOfWeek(now, { locale: fr });
      const weekEnd = endOfWeek(now, { locale: fr });

      const { data, error } = await supabase
        .from("validated_amounts")
        .select("amount")
        .eq("owner_id", user.id)
        .gte("counting_date", weekStart.toISOString().split("T")[0])
        .lte("counting_date", weekEnd.toISOString().split("T")[0]);

      if (error) throw error;
      return data.reduce((sum, record) => sum + record.amount, 0);
    },
  });

  const { data: monthlyTotal = 0 } = useQuery({
    queryKey: ["validatedAmounts", "month"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const now = new Date();
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);

      const { data, error } = await supabase
        .from("validated_amounts")
        .select("amount")
        .eq("owner_id", user.id)
        .gte("counting_date", monthStart.toISOString().split("T")[0])
        .lte("counting_date", monthEnd.toISOString().split("T")[0]);

      if (error) throw error;
      return data.reduce((sum, record) => sum + record.amount, 0);
    },
  });

  const totalAmount = validatedAmounts.reduce(
    (sum, item) => sum + Number(item.amount),
    0
  );

  if (isLoading) {
    return <div>Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <h3 className="text-lg font-semibold mb-2">
              Total des financements validés
            </h3>
            <p className="text-2xl font-bold text-primary">
              {totalAmount.toLocaleString()} FCFA
            </p>
          </div>
          
          <div>
            <h3 className="text-lg font-semibold mb-2">
              Total de la semaine
            </h3>
            <p className="text-2xl font-bold text-primary">
              {weeklyTotal.toLocaleString()} FCFA
            </p>
          </div>

          <div>
            <h3 className="text-lg font-semibold mb-2">
              Total du mois
            </h3>
            <p className="text-2xl font-bold text-primary">
              {monthlyTotal.toLocaleString()} FCFA
            </p>
          </div>
        </div>

        <div className="mt-6">
          <FinancingFilters
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
          />

          <FinancingTable validatedAmounts={validatedAmounts} />
        </div>
      </Card>
    </div>
  );
};